import { ReactNode, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { animate, motion, useMotionValue } from 'framer-motion';
import { isMobileOnly } from 'react-device-detect';

import { FacingMode, UserRole } from 'types';
import { stringToDate } from 'utils';
import { eventSessionSelector, metaSelector, userSelector } from 'store/selectors';
import { tracker } from 'services';
import { useStore } from 'hooks';
import { RecordingIndicator, Text } from 'components';
import { CameraSwitchIcon, ChevronDownIcon, LiveWaveIcon } from 'components/Icon';

import { useLiveEvent } from './LiveEvent.context';
import Info from './LiveEvent.Info';

import styles from './LiveEvent.TopBar.module.scss';

export type TopBarProps = {
  cameraSwitchDisabled?: boolean;
  isConnectionStable?: boolean;
  isRecordingEnabled?: boolean;
  onToggleFacingMode?(): Promise<FacingMode>;
  onLeave?(): void;
  onEnd?(): void;
  onExit?(): void;
};

const TopBar = ({
  cameraSwitchDisabled = false,
  isConnectionStable = true,
  isRecordingEnabled = false,
  onToggleFacingMode,
  onLeave,
  onEnd,
  onExit
}: TopBarProps) => {
  const liveEvent = useLiveEvent();
  const eventSession = useStore(eventSessionSelector(liveEvent.eventCode));
  const eventMeta = useStore(metaSelector(liveEvent.eventCode));
  const localUser = useStore(userSelector(liveEvent.eventCode, eventSession.username));

  const transformTop = useMotionValue(0);
  const [isCameraSwitching, setCameraSwitching] = useState(false);
  const [isInfoOpen, setInfoOpen] = useState(false);
  const unstableTextRef = useRef<HTMLDivElement>(null);

  const unsupportedTextHeight = unstableTextRef.current?.clientHeight || 34;

  useEffect(() => {
    if (!isConnectionStable) {
      animate(transformTop, 0, { type: 'spring', stiffness: 400 });
    } else {
      animate(transformTop, unsupportedTextHeight, { type: 'spring', stiffness: 400 });
    }
  }, [isConnectionStable]);

  const handleOnCameraSwitch = async () => {
    setCameraSwitching(true);
    await onToggleFacingMode?.();
    setCameraSwitching(false);
  };

  const toggleInfo = () => {
    if (!isInfoOpen) {
      tracker.openEventInfoPanel(liveEvent.eventCode);
    }

    setInfoOpen(prev => !prev);
  };

  const renderLeftItems = () => {
    let content: ReactNode | null = null;

    switch (localUser.role) {
      case UserRole.HOST:
      case UserRole.MODERATOR:
      case UserRole.SPEAKER:
        if (isMobileOnly) {
          content = (
            <button
              type="button"
              className={styles.camera}
              disabled={cameraSwitchDisabled || isCameraSwitching}
              onClick={handleOnCameraSwitch}
            >
              <CameraSwitchIcon />
            </button>
          );
        }
        break;
      case UserRole.VIEWER:
        content = (
          <div className={styles.live}>
            <LiveWaveIcon className={styles.wave} />
            <span>Live</span>
          </div>
        );
    }

    return (
      <>
        {content}
        {isRecordingEnabled && (
          <div className="ml-10">
            <RecordingIndicator />
          </div>
        )}
      </>
    );
  };

  const renderAbout = () => (
    <button type="button" className={cx(styles.about, { [styles.open]: isInfoOpen })} onClick={toggleInfo}>
      <span>About</span>
      <ChevronDownIcon />
    </button>
  );

  const renderExitButton = () => (
    <button type="button" className={styles.exit} onClick={onExit}>
      Exit
    </button>
  );

  const renderLeaveStageButton = () => (
    <button type="button" className={styles.leave} onClick={onLeave}>
      Leave
    </button>
  );

  const renderEndButton = () => (
    <button type="button" className={styles.end} onClick={onEnd}>
      End
    </button>
  );

  const renderLeave = () => {
    switch (localUser.role) {
      case UserRole.HOST:
        return renderEndButton();

      case UserRole.MODERATOR:
      case UserRole.SPEAKER:
        return renderLeaveStageButton();

      case UserRole.VIEWER:
        return renderExitButton();

      default:
        return null;
    }
  };

  return (
    <motion.div
      className={styles.container}
      transition={{
        y: { type: 'spring', stiffness: 150, damping: 20, mass: 1 }
      }}
      initial={{ y: -1 * unsupportedTextHeight }}
      animate={isConnectionStable ? { y: -1 * unsupportedTextHeight } : { y: 0 }}
    >
      <Text ref={unstableTextRef} size="h3" className={styles.unstable}>
        Your connection is unstable
      </Text>

      <div className={cx(styles.content, { [styles.open]: isInfoOpen })}>
        <div className={styles.left}>{renderLeftItems()}</div>
        <div className={styles.center}>{renderAbout()}</div>
        <div className={styles.right}>{renderLeave()}</div>
      </div>

      <Info
        show={isInfoOpen}
        eventName={eventMeta.name}
        authorName={eventMeta.hostName}
        startDate={stringToDate(eventMeta.startDate)}
      />
    </motion.div>
  );
};

export default TopBar;
